import { serializeAppData } from "./app-data-envelope";
import { portableAppDataSchema } from "../domain/app-data-schema";
import { emptyAppData } from "../domain/types";
import { APP_DATA_STORAGE_KEY as KEY, RECOVERY_KEY_PREFIX, TOSS_RECOVERY_INDEX_KEY } from "./storage-keys";

export interface NativeStoragePort {
  getItem(key: string): Promise<string | null | undefined>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}
export type TossStorageState =
  | { status: "web" | "loading" | "synced" | "pending" | "error" }
  | { status: "conflict"; localRaw: string | null; durableRaw: string | null };
export const canOpenStoredCopy = (raw: string | null) => {
  if (raw === null) return true;
  try { const parsed = JSON.parse(raw); return (parsed?.version === 1 || parsed?.version === 2 || parsed?.version === 3) && portableAppDataSchema.safeParse(parsed).success; }
  catch { return false; }
};

/** Serializes only this app instance's bridge work. Native Storage has no CAS;
 * different copies are never ranked by time, size or presumed freshness. */
export class TossStorageCoordinator {
  state: TossStorageState = { status: "loading" };
  private expectedDurable: string | null | undefined;
  private sequence = Promise.resolve();
  private generation = 0;
  constructor(private port: NativeStoragePort, private local: Storage, private changed: () => void) {}
  private setState(state: TossStorageState) { this.state = state; this.changed(); }
  private enqueue<T>(work: () => Promise<T>): Promise<T> {
    const operation = this.sequence.then(work);
    this.sequence = operation.then(() => undefined, () => undefined);
    return operation;
  }
  private async nativeRaw() { return (await this.port.getItem(KEY)) ?? null; }
  private conflict(localRaw: string | null, durableRaw: string | null) { this.setState({ status: "conflict", localRaw, durableRaw }); }
  private async archiveIndex(): Promise<string[]> {
    const raw = await this.port.getItem(TOSS_RECOVERY_INDEX_KEY);
    if (raw == null) return [];
    const keys: unknown = JSON.parse(raw);
    if (!Array.isArray(keys) || keys.some((key) => typeof key !== "string" || !key.startsWith(RECOVERY_KEY_PREFIX))) throw new Error("Invalid recovery index");
    return keys;
  }
  private async restoreArchives() {
    for (const key of await this.archiveIndex()) {
      const raw = await this.port.getItem(key);
      if (raw == null) throw new Error("Missing recovery copy");
      const existing = this.local.getItem(key);
      // Never replace a local archive even if the host returns different bytes.
      const target = existing !== null && existing !== raw ? `${RECOVERY_KEY_PREFIX}retained-${crypto.randomUUID()}` : key;
      this.local.setItem(target, raw);
      if (this.local.getItem(target) !== raw) throw new Error("Recovery read-back failed");
    }
  }
  private async archiveBoth(localRaw: string | null, durableRaw: string | null) {
    const keys = await this.archiveIndex();
    for (const [label, raw] of [["device", localRaw], ["toss", durableRaw]] as const) {
      if (raw === null) continue;
      const key = `${RECOVERY_KEY_PREFIX}${label}-${crypto.randomUUID()}`;
      this.local.setItem(key, raw);
      if (this.local.getItem(key) !== raw) throw new Error("Local original archive failed");
      await this.port.setItem(key, raw);
      if (await this.port.getItem(key) !== raw) throw new Error("Native original archive failed");
      keys.push(key);
      const index = JSON.stringify(keys);
      await this.port.setItem(TOSS_RECOVERY_INDEX_KEY, index);
      if (await this.port.getItem(TOSS_RECOVERY_INDEX_KEY) !== index) throw new Error("Recovery index read-back failed");
    }
  }
  initialize() {
    return this.enqueue(async () => {
      try {
        const wasConflicted = this.state.status === "conflict";
        await this.restoreArchives();
        const durable = await this.nativeRaw();
        const local = this.local.getItem(KEY);
        this.expectedDurable = durable;
        // Refreshing a conflict only refreshes its choices. The App must re-read
        // the selected copy before autosave can resume, even if copies now match.
        if (wasConflicted || (local !== null && durable !== null && local !== durable)) { this.conflict(local, durable); return; }
        if (local === null && durable !== null) {
          this.local.setItem(KEY, durable);
          if (this.local.getItem(KEY) !== durable) throw new Error("Local import read-back failed");
        }
        if (durable === null && local !== null) {
          // Malformed data belongs to the recovery screen, never to auto-sync.
          if (!canOpenStoredCopy(local)) { this.setState({ status: "error" }); return; }
          await this.flush(local, this.generation);
        } else this.setState({ status: "synced" });
      } catch { this.setState({ status: "error" }); }
    });
  }
  mirror(raw: string | null) {
    const generation = ++this.generation;
    if (this.state.status === "conflict") return Promise.resolve();
    this.setState({ status: "pending" });
    return this.enqueue(() => this.flush(raw, generation));
  }
  private async flush(raw: string | null, generation: number) {
    if (generation !== this.generation || this.state.status === "conflict") return;
    try {
      if (!canOpenStoredCopy(raw)) throw new Error("Invalid copy");
      const durable = await this.nativeRaw();
      const local = this.local.getItem(KEY);
      if (generation !== this.generation) return;
      if (local !== raw) { this.conflict(local, durable); return; }
      if ((this.expectedDurable === undefined && durable !== null && durable !== raw) || (this.expectedDurable !== undefined && durable !== this.expectedDurable && durable !== raw)) {
        this.conflict(local, durable); return;
      }
      if (durable !== raw) {
        if (raw === null) await this.port.removeItem(KEY);
        else await this.port.setItem(KEY, raw);
      }
      this.expectedDurable = raw;
      const observed = await this.nativeRaw();
      if (observed !== raw) { this.conflict(this.local.getItem(KEY), observed); return; }
      this.setState({ status: generation === this.generation ? "synced" : "pending" });
    } catch { this.setState({ status: "error" }); }
  }
  retry() { return this.state.status === "conflict" ? this.initialize() : this.mirror(this.local.getItem(KEY)); }
  archiveBeforeReset(expectedRaw: string) {
    ++this.generation;
    return this.enqueue(async () => {
      const durable = await this.nativeRaw();
      const local = this.local.getItem(KEY);
      if (local !== expectedRaw || (durable !== null && durable !== expectedRaw)) {
        this.conflict(local, durable);
        throw new Error("Originals changed before recovery");
      }
      await this.archiveBoth(local, durable);
    });
  }
  resolve(choice: "local" | "toss" | "empty") {
    const conflict = this.state;
    ++this.generation; // Invalidate queued snapshots before any explicit decision.
    return this.enqueue(async () => {
      if (conflict.status !== "conflict") return false;
      try {
        const durable = await this.nativeRaw();
        const local = this.local.getItem(KEY);
        if (local !== conflict.localRaw || durable !== conflict.durableRaw) { this.conflict(local, durable); return false; }
        const raw = choice === "empty" ? serializeAppData(emptyAppData) : (choice === "local" ? local : durable);
        if (!canOpenStoredCopy(raw)) return false;
        await this.archiveBoth(local, durable);
        if (this.local.getItem(KEY) !== local || await this.nativeRaw() !== durable) { this.conflict(this.local.getItem(KEY), await this.nativeRaw()); return false; }
        if (raw === null) this.local.removeItem(KEY); else this.local.setItem(KEY, raw);
        if (this.local.getItem(KEY) !== raw) throw new Error("Selection read-back failed");
        this.expectedDurable = durable;
        this.setState({ status: "pending" });
        await this.flush(raw, this.generation);
        return true;
      } catch {
        // Keep choices visible when archiving fails; no original is discarded.
        this.setState(conflict);
        return false;
      }
    });
  }
  deleteAll() {
    ++this.generation;
    return this.enqueue(async () => {
      try {
        for (const key of await this.archiveIndex()) {
          await this.port.removeItem(key);
          if (await this.port.getItem(key) != null) throw new Error("Archive deletion read-back failed");
        }
        await this.port.removeItem(TOSS_RECOVERY_INDEX_KEY);
        await this.port.removeItem(KEY);
        if (await this.nativeRaw() !== null || await this.port.getItem(TOSS_RECOVERY_INDEX_KEY) != null) throw new Error("Deletion read-back failed");
        this.expectedDurable = null;
        this.setState({ status: "synced" });
      } catch (error) { this.setState({ status: "error" }); throw error; }
    });
  }
}
